import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  inject,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'

import { EMPTY, catchError, switchMap } from 'rxjs'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxFormControlModule,
  FlxIconModule,
  FlxModalModule,
  FlxModalRef,
} from '@flowx/angular-ui-toolkit'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import { FlxAutofocusDirective } from '@flowx/designer/shared/directives/autofocus.directive'

import { EnterSubmitFormDirective } from '../../directives/enter-submit-form.directive'
import { ApplicationResourceTypeClientFriendlyNames } from '../../entities/application.entity'
import { CopyResourceSseMessageType } from '../../model/copy-resource.model'
import { DuplicateResourceService } from '../../services/duplicate-resource.service'
import { ToastMessageService } from '../../services/toast-message.service'

@Component({
  selector: 'flx-duplicate-resource',
  templateUrl: './duplicate-resource.component.html',
  imports: [
    ReactiveFormsModule,
    FlxModalModule,
    FlxIconModule,
    FlxSpinnerModule,
    FlxFormControlModule,
    FlxButtonModule,
    FlxAutofocusDirective,
    EnterSubmitFormDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DuplicateResourceService],
})
export class DuplicateResourceComponent {
  #duplicateResourceService = inject(DuplicateResourceService)
  #data = inject(FLX_MODAL_DATA)
  #modalRef = inject(FlxModalRef)
  #destroyRef = inject(DestroyRef)
  #toastMsgService = inject(ToastMessageService)
  @Output() dataEventEmitter = new EventEmitter<string>()

  loading = this.#duplicateResourceService.loading
  resourceName = this.#data.resourceName
  resourceType =
    ApplicationResourceTypeClientFriendlyNames[this.#data.params.resourceType] || 'Resource'
  title = `Duplicate ${this.resourceType}`
  duplicateResourceFG = new FormGroup({
    name: new FormControl(`Copy_of_${this.resourceName}`, {
      validators: [Validators.required],
      nonNullable: true,
    }),
  })

  constructor() {
    this.#duplicateResourceService.params.set(this.#data.params)
  }

  get nameFC(): FormControl<string> {
    return this.duplicateResourceFG.controls.name
  }

  closeModal(): void {
    this.#modalRef.close()
  }

  submitModal(): void {
    if (this.duplicateResourceFG.valid) {
      this.loading.set(true)
      let newResourceId: string
      this.#duplicateResourceService
        .duplicateResource(this.nameFC.value)
        .pipe(
          switchMap(({ copyId }) => {
            newResourceId = copyId
            return this.#duplicateResourceService.sseConnection(copyId, this.resourceType)
          }),
          catchError((err) => {
            this.loading.set(false)
            this.#toastMsgService.showErrorMessage(err?.error?.title)
            return EMPTY
          }),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe((sseMessageType) => {
          switch (sseMessageType) {
            case CopyResourceSseMessageType.SUCCESS: {
              this.dataEventEmitter.emit(newResourceId)
              this.closeModal()
              break
            }
            case CopyResourceSseMessageType.ERROR:
              this.#toastMsgService.showErrorMessage(
                'An error occurred while duplicating the resource. Please try again.'
              )
              break
          }
        })
    }
  }
}
